import { MONTHS } from '../types';
import type { ForecastAccount, MonthlyExpandedValues } from '../types';

/**
 * Department-spread basis — one place for the modal and the inline grid path
 * so a department total spreads identically no matter how it was entered.
 *
 * Sign conventions on MonthlyExpandedValues:
 *  - budget / lyBudget: display-signed (revenue +, expense −)
 *  - actual / lyActual: ledger-signed (revenue −, expense +)
 * The basis is a proportion, so every source is normalised to a positive
 * magnitude per account before allocating.
 */
export type SpreadBasis = 'budget' | 'lyBudget' | 'lyActual' | 'actual';

export const SPREAD_BASES: SpreadBasis[] = ['budget', 'lyBudget', 'lyActual', 'actual'];

export const isSpreadBasis = (v: unknown): v is SpreadBasis =>
  typeof v === 'string' && (SPREAD_BASES as string[]).includes(v);

/** Preference order when the chosen basis has no data (or nothing is saved). */
export function spreadBasisPreference(isBudget: boolean): SpreadBasis[] {
  return isBudget
    ? ['lyBudget', 'lyActual', 'actual', 'budget']
    : ['budget', 'lyBudget', 'lyActual', 'actual'];
}

export function spreadBasisStorageKey(versionId: number) {
  return `lodgeic_spread_basis_v${versionId}`;
}

export function readSavedSpreadBasis(versionId: number): SpreadBasis | null {
  try {
    const val = localStorage.getItem(spreadBasisStorageKey(versionId));
    return isSpreadBasis(val) ? val : null;
  } catch { return null; }
}

export function saveSpreadBasis(versionId: number, basis: SpreadBasis) {
  try { localStorage.setItem(spreadBasisStorageKey(versionId), basis); } catch {}
}

export function clearSpreadBasis(versionId: number) {
  try { localStorage.removeItem(spreadBasisStorageKey(versionId)); } catch {}
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** Human labels for the basis picker / warnings. `month` is 1-12. */
export function spreadBasisLabel(basis: SpreadBasis, year: number, month: number) {
  const monthName = MONTH_NAMES[month - 1];
  const prevName = MONTH_NAMES[month <= 1 ? 11 : month - 2];
  const prevYear = month <= 1 ? year - 1 : year;
  switch (basis) {
    case 'budget':
      return { label: '% of Budget', description: `Distribute proportionally based on the ${year} published budget for ${monthName}` };
    case 'lyBudget':
      return { label: '% of PY Published Budget', description: `Distribute proportionally based on the ${year - 1} published budget for ${monthName}` };
    case 'lyActual':
      return { label: '% of LY Same Month Actual', description: `Distribute proportionally based on ${monthName} ${year - 1} actual amounts` };
    case 'actual':
      return { label: '% of Last Month Actual', description: `Distribute proportionally based on ${prevName} ${prevYear} actual amounts` };
  }
}

/** Short source name for the "no data" warning. */
export function spreadBasisSourceName(basis: SpreadBasis, year: number, month: number) {
  const monthName = MONTH_NAMES[month - 1];
  const prevName = MONTH_NAMES[month <= 1 ? 11 : month - 2];
  switch (basis) {
    case 'budget': return `${year} budget`;
    case 'lyBudget': return `${year - 1} published budget`;
    case 'lyActual': return `${monthName} ${year - 1} actual`;
    case 'actual': return `${prevName} actual`;
  }
}

/** Positive magnitude of one account's basis value for the given month (1-12). */
export function spreadBasisValue(
  account: Pick<ForecastAccount, 'monthlyExpanded'>,
  basis: SpreadBasis,
  month: number,
): number {
  const monthKey = MONTHS[month - 1];
  const prevMonthKey = MONTHS[month <= 1 ? 11 : month - 2];
  const cur: Partial<MonthlyExpandedValues> = account.monthlyExpanded?.[monthKey] ?? {};
  // "Last month actual" for January reads December of the same expanded map,
  // which the grid populates from the prior year's closed December.
  const prev: Partial<MonthlyExpandedValues> = account.monthlyExpanded?.[prevMonthKey] ?? {};
  const raw =
    basis === 'budget' ? cur.budget
    : basis === 'lyBudget' ? cur.lyBudget
    : basis === 'lyActual' ? cur.lyActual
    : prev.actual;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.abs(n) : 0;
}

export function spreadBasisValues(
  accounts: Pick<ForecastAccount, 'monthlyExpanded'>[],
  basis: SpreadBasis,
  month: number,
): number[] {
  return accounts.map(a => spreadBasisValue(a, basis, month));
}

/** True when at least one account has a non-zero value for this basis. */
export function spreadBasisHasData(
  accounts: Pick<ForecastAccount, 'monthlyExpanded'>[],
  basis: SpreadBasis,
  month: number,
): boolean {
  return accounts.some(a => spreadBasisValue(a, basis, month) !== 0);
}

/**
 * Resolve the basis to use: the requested one when it has data, otherwise the
 * first basis in preference order that does. Returns null when nothing has data.
 */
export function resolveSpreadBasis(
  accounts: Pick<ForecastAccount, 'monthlyExpanded'>[],
  month: number,
  requested: SpreadBasis | null,
  isBudget: boolean,
): { basis: SpreadBasis; fellBack: boolean } | null {
  if (requested && spreadBasisHasData(accounts, requested, month)) {
    return { basis: requested, fellBack: false };
  }
  for (const b of spreadBasisPreference(isBudget)) {
    if (b === requested) continue;
    if (spreadBasisHasData(accounts, b, month)) return { basis: b, fellBack: requested != null };
  }
  return null;
}

/** Proportional allocation of `total` across accounts by basis magnitude. */
export function spreadAllocations<T extends Pick<ForecastAccount, 'monthlyExpanded'>>(
  accounts: T[],
  basis: SpreadBasis,
  month: number,
  total: number,
): { account: T; basisValue: number; pct: number; allocated: number }[] {
  const values = spreadBasisValues(accounts, basis, month);
  const sum = values.reduce((s, v) => s + v, 0);
  return accounts.map((account, i) => ({
    account,
    basisValue: values[i],
    pct: sum === 0 ? 0 : (values[i] / sum) * 100,
    allocated: sum === 0 ? 0 : (values[i] / sum) * total,
  }));
}
